import React from "react";
import { useParams } from "react-router-dom";
import { ButtonSwitch, PaziaBox, PaziaLoader } from "components/atoms";
import { HeaderWithBreadcrumbs, PageMain, TanTableClient, TanTableColumn } from "components/molecules";
import { useCourse } from "api/hooks";
import {
  CourseFinanceReconciliationApi,
  ReconciliationExamPaymentApi,
  ReconciliationManualEntryApi,
  ReconciliationRefundApi,
  ReconciliationStudentApi,
  ReconciliationTrainerCostApi,
  useCourseFinanceReconciliation,
} from "api/course-finances";
import { routes } from "consts";
import { StudentReconciliationCard } from "./StudentReconciliationCard";
import { courseWarningLabel, dateTime, money, paymentStatusLabel, refundStatusLabel, signedMoney } from "./reconciliationLabels";

const ONLY_PROBLEMS = "Tylko z problemami";
const ALL_STUDENTS = "Wszyscy kursanci";

const sectionTitleClasses = "mb-3 text-lg font-semibold text-slate-800 dark:text-slate-100";
const emptyClasses = "text-sm text-slate-500 dark:text-slate-400";
const redText = "text-rose-600 dark:text-rose-400";

const Tile = ({ label, value, valueClassName }: { label: string; value: React.ReactNode; valueClassName?: string }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-lg dark:border-slate-700 dark:bg-slate-800">
    <div className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">{label}</div>
    <div className={`mt-1 text-xl font-bold ${valueClassName ?? "text-slate-800 dark:text-slate-100"}`}>{value}</div>
  </div>
);

const Section = ({ title, children }: { title: React.ReactNode; children: React.ReactNode }) => (
  <PaziaBox>
    <h2 className={sectionTitleClasses}>{title}</h2>
    {children}
  </PaziaBox>
);

const SectionTable = <T,>({ data, columns }: { data: T[]; columns: TanTableColumn<T>[] }) =>
  data.length === 0 ? (
    <div className={emptyClasses}>Brak</div>
  ) : (
    <TanTableClient data={data} columns={columns} hideBorder hidePagination pageSize={1000} />
  );

const examColumns: TanTableColumn<ReconciliationExamPaymentApi>[] = [
  { id: "paymentId", header: "#Id", accessorKey: "paymentId", cell: (c) => `#${c.getValue<number>()}` },
  { id: "studentName", header: "Kursant", accessorKey: "studentName" },
  { id: "examName", header: "Egzamin", accessorKey: "examName", cell: (c) => c.getValue<string | null>() || "—" },
  { id: "countedAmount", header: "Kwota", accessorKey: "countedAmount", cell: (c) => money(c.getValue<number>()) },
  { id: "status", header: "Status", accessorKey: "status", cell: (c) => paymentStatusLabel(c.getValue<number>()) },
  { id: "createdDate", header: "Data", accessorKey: "createdDate", cell: (c) => dateTime(c.getValue<Date | string>()) },
];

const refundColumns: TanTableColumn<ReconciliationRefundApi>[] = [
  { id: "refundPaymentId", header: "Zwrot", accessorKey: "refundPaymentId", cell: (c) => `#${c.getValue<number>()}` },
  {
    id: "originalPaymentId",
    header: "Do płatności",
    accessorKey: "originalPaymentId",
    cell: (c) => `#${c.getValue<number>()}`,
  },
  { id: "studentName", header: "Kursant", accessorKey: "studentName" },
  { id: "rawAmount", header: "Kwota", accessorKey: "rawAmount" },
  {
    id: "refundStatus",
    header: "Status zwrotu",
    accessorKey: "refundStatus",
    cell: (c) => {
      const v = c.getValue<number | null>();
      return v == null ? "—" : refundStatusLabel(v);
    },
  },
  { id: "createdDate", header: "Data", accessorKey: "createdDate", cell: (c) => dateTime(c.getValue<Date | string>()) },
];

const trainerCostColumns: TanTableColumn<ReconciliationTrainerCostApi>[] = [
  { id: "trainerName", header: "Trener", accessorKey: "trainerName" },
  { id: "scopeLabel", header: "Zakres", accessorKey: "scopeLabel" },
  { id: "value", header: "Kwota", accessorKey: "value", cell: (c) => money(c.getValue<number>()) },
  { id: "date", header: "Data", accessorKey: "date", cell: (c) => dateTime(c.getValue<Date | string>()) },
];

const manualEntryColumns: TanTableColumn<ReconciliationManualEntryApi>[] = [
  { id: "name", header: "Nazwa", accessorKey: "name" },
  { id: "category", header: "Kategoria", accessorKey: "category" },
  { id: "value", header: "Kwota", accessorKey: "value", cell: (c) => money(c.getValue<number>()) },
  { id: "date", header: "Data", accessorKey: "date", cell: (c) => dateTime(c.getValue<Date | string>()) },
];

const CourseWarnings = ({ data }: { data: CourseFinanceReconciliationApi }) => {
  const studentNames = new Map(
    [...data.students, ...data.removedStudents].map((s) => [s.registrationId, `${s.firstName} ${s.surname}`]),
  );
  return (
    <Section title="Ostrzeżenia kursu">
      <ul className="space-y-2">
        {data.courseWarnings.map((w, i) => (
          <li
            key={i}
            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300"
          >
            <span className="font-semibold">{courseWarningLabel(w.code)}</span>
            {[
              w.registrationId != null ? (studentNames.get(w.registrationId) ?? `zapis #${w.registrationId}`) : null,
              w.paymentId != null ? `płatność #${w.paymentId}` : null,
              w.amount != null ? money(w.amount) : null,
            ]
              .filter(Boolean)
              .map((part) => ` · ${part}`)
              .join("")}
          </li>
        ))}
      </ul>
    </Section>
  );
};

const plural = (n: number, one: string, few: string, many: string) => {
  if (n === 1) return one;
  const mod10 = n % 10;
  const mod100 = n % 100;
  return mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14) ? few : many;
};

const StudentList = ({
  title,
  students,
  visible,
  isRemoved,
}: {
  title: string;
  students: ReconciliationStudentApi[];
  visible: ReconciliationStudentApi[];
  isRemoved?: boolean;
}) => (
  <Section
    title={
      <>
        {title}{" "}
        <span className="text-sm font-normal text-slate-500 dark:text-slate-400">
          ({visible.length} z {students.length})
        </span>
      </>
    }
  >
    {visible.length === 0 ? (
      <div className={emptyClasses}>Brak</div>
    ) : (
      <div className="space-y-3">
        {visible.map((s) => (
          <StudentReconciliationCard key={s.registrationId} student={s} isRemoved={isRemoved} />
        ))}
      </div>
    )}
  </Section>
);

/** „Rozliczenie” — read-only reconciliation of a course's tranches, payments and balance. */
export const CourseReconciliationPage = () => {
  const { courseId } = useParams();
  const { loading: loadingCourse, data: course } = useCourse(Number(courseId));
  const { loading, data } = useCourseFinanceReconciliation(Number(courseId));
  const [filter, setFilter] = React.useState(ONLY_PROBLEMS);

  if (loadingCourse) return <PaziaLoader position="center" />;
  if (!course) return null;

  const onlyProblems = filter === ONLY_PROBLEMS;
  const warnedRegistrationIds = new Set(
    (data?.courseWarnings ?? []).map((w) => w.registrationId).filter((id): id is number => id != null),
  );
  const hasProblems = (s: ReconciliationStudentApi) =>
    s.issues.length > 0 || s.unexplained !== 0 || warnedRegistrationIds.has(s.registrationId);
  const visibleOf = (students: ReconciliationStudentApi[]) => (onlyProblems ? students.filter(hasProblems) : students);

  return (
    <PageMain>
      <HeaderWithBreadcrumbs
        breadcrumbs={[
          { title: "Kursy", path: routes.COURSES },
          {
            title: [course.data.name, course.data.variant].filter(Boolean).join(" · "),
            path: routes.COURSES_MANAGE(course.data.id),
          },
          { title: "Zestawienie finansowe", path: routes.COURSES_MANAGE_NESTED.FINANCES(course.data.id) },
          { title: "Rozliczenie" },
        ]}
        headerText="Rozliczenie"
      />
      {loading ? (
        <PaziaLoader />
      ) : !data ? (
        <div className={emptyClasses}>Nie udało się wczytać rozliczenia.</div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-7">
            <Tile label="Bilans (jak na wykresie)" value={money(data.summary.balance)} />
            <Tile label="Należne za opłacone transze" value={money(data.summary.dueForPaidTranches)} />
            <Tile label="Przychód z zapisów" value={money(data.summary.registrationRevenue)} />
            <Tile
              label="Różnica"
              value={signedMoney(data.summary.difference)}
              valueClassName={data.summary.difference !== 0 ? redText : undefined}
            />
            <Tile label="Pozostało do zapłaty" value={money(data.summary.remainingToPay)} />
            <Tile label="Koszty" value={money(data.summary.costs)} />
            <Tile
              label="Problemy"
              value={
                <span className="text-base">
                  <span className={data.summary.errorCount > 0 ? redText : undefined}>
                    {data.summary.errorCount} {plural(data.summary.errorCount, "błąd", "błędy", "błędów")}
                  </span>
                  {" / "}
                  <span className={data.summary.warningCount > 0 ? "text-amber-600 dark:text-amber-400" : undefined}>
                    {data.summary.warningCount}{" "}
                    {plural(data.summary.warningCount, "ostrzeżenie", "ostrzeżenia", "ostrzeżeń")}
                  </span>
                </span>
              }
            />
          </div>

          {data.courseWarnings.length > 0 && <CourseWarnings data={data} />}

          <ButtonSwitch a={ONLY_PROBLEMS} b={ALL_STUDENTS} value={filter} onChange={setFilter} className="max-w-md" />

          <StudentList title="Kursanci" students={data.students} visible={visibleOf(data.students)} />
          <StudentList
            title="Usunięci kursanci"
            students={data.removedStudents}
            visible={visibleOf(data.removedStudents)}
            isRemoved
          />

          <Section title="Egzaminy">
            <SectionTable data={data.examPayments} columns={examColumns} />
          </Section>
          <Section title="Zwroty">
            <SectionTable data={data.refunds} columns={refundColumns} />
          </Section>
          <Section title="Koszty trenerów">
            <SectionTable data={data.trainerCosts} columns={trainerCostColumns} />
          </Section>
          <Section title="Pozycje ręczne">
            <SectionTable data={data.manualEntries} columns={manualEntryColumns} />
          </Section>
        </div>
      )}
    </PageMain>
  );
};
